import { useEffect, useState, type ReactElement } from "react";

import { api } from "../../api/client";
import { longDate } from "../../lib/date";
import { PdfViewer } from "../PdfViewer";
import { SkeletonRows } from "../Skeleton";
import { TabPanel } from "../Tabs";
import type { ConsentDocumentOut, ParticipantProfile, SignedConsentOut } from "../../api/types";

// Read-only "your signed consent": the attestation on file (name, DOB, patient
// status, document version, signed date) + the participant's own signed PDF
// (falling back to the blank canonical sheet when no signed copy is stored).

function patientLabel(status: number): string {
  if (status === 1) return "Current or former Mass General Brigham patient";
  if (status === 0) return "Not a Mass General Brigham patient";
  return "Prefer not to answer";
}

export function ConsentCopy({ profile }: { profile: ParticipantProfile }): ReactElement {
  const consented = profile.enrollment_status === "active";
  const [doc, setDoc] = useState<ConsentDocumentOut | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!consented) return;
    void (async () => {
      // The participant's own signed copy (information sheet + attestation
      // page) is what renders; the blank template is only the fallback for the
      // rare account with no signed PDF on file (pre-feature consents).
      try {
        const s = await api<SignedConsentOut>("GET", "/participants/me/consent/signed");
        if (s.url) {
          setSignedUrl(s.url);
          return;
        }
      } catch {
        /* network/auth error — fall back to the template below */
      }
      try {
        // Param-less: the server resolves the study consent or the stored
        // patient status — the exact sheet the participant agreed to.
        const d = await api<ConsentDocumentOut>("GET", "/participants/me/consent/document");
        setDoc(d);
      } catch {
        setError("Could not load your consent document.");
      }
    })();
  }, [consented]);

  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "—";

  return (
    <TabPanel id="consent" title="Your Signed Consent">
      {profile.enrollment_status !== "active" ? (
        <p className="muted">You haven't completed consent yet.</p>
      ) : (
        <>
          <dl className="account-readonly">
            <div>
              <dt>Name on file</dt>
              <dd>{fullName}</dd>
            </div>
            <div>
              <dt>Date of birth</dt>
              <dd>{profile.date_of_birth ? longDate(profile.date_of_birth) : "—"}</dd>
            </div>
            {profile.mgb_patient_status !== null ? (
              <div>
                <dt>Patient status</dt>
                <dd>{patientLabel(profile.mgb_patient_status)}</dd>
              </div>
            ) : null}
            <div>
              <dt>Document version</dt>
              <dd>{profile.consent_version ?? "—"}</dd>
            </div>
            <div>
              <dt>Signed</dt>
              <dd>{profile.consented_at ? longDate(profile.consented_at) : "—"}</dd>
            </div>
          </dl>

          {error ? <p className="form-error">{error}</p> : null}
          {signedUrl ? (
            <>
              <p className="consent-doc-head">
                <span className="muted">
                  Your signed copy (the information sheet + your attestation)
                </span>
                <a className="btn-ghost btn-sm" href={signedUrl} target="_blank" rel="noreferrer">
                  Download signed copy
                </a>
              </p>
              <PdfViewer file={signedUrl} title="Signed Consent Document" />
            </>
          ) : doc ? (
            <>
              <p className="consent-doc-head">
                <span className="muted">
                  The information sheet you agreed to. Your individually signed copy is
                  not yet on file.
                </span>
                <a className="btn-ghost btn-sm" href={doc.url} target="_blank" rel="noreferrer">
                  Download PDF
                </a>
              </p>
              <PdfViewer file={doc.url} title="Consent Information Sheet" />
            </>
          ) : !error ? (
            <SkeletonRows rows={6} />
          ) : null}
        </>
      )}
    </TabPanel>
  );
}
